import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, AtSign, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useCommunityHandle, HANDLE_REGEX } from '@/hooks/useCommunityHandle';
import { AVATAR_PRESETS } from '@/components/community/avatars';
import { LocationEditor } from '@/components/community/LocationEditor';

// The Club's community handle, avatar and map pin (moved from the Account tab to the Club tab, 30 Oct 2026).
export function CommunityCard({ user }: { user: User }) {
  const { toast } = useToast();

  const { handle, avatarId, saveHandle, saveAvatar, checkAvailable } = useCommunityHandle(user);
  const [handleInput, setHandleInput] = useState('');
  const [savingAvatar, setSavingAvatar] = useState<string | null>(null);
  const [savingHandle, setSavingHandle] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => { setHandleInput(handle ?? ''); }, [handle]);

  useEffect(() => {
    const trimmed = handleInput.trim();
    if (!trimmed || trimmed === handle) { setAvailable(null); return; }
    if (!HANDLE_REGEX.test(trimmed)) { setAvailable(false); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      setAvailable(await checkAvailable(trimmed));
      setChecking(false);
    }, 350);
    return () => clearTimeout(t);
  }, [handleInput, handle, checkAvailable]);

  const handleSaveHandle = async () => {
    setSavingHandle(true);
    const res = await saveHandle(handleInput.trim());
    setSavingHandle(false);
    if (!res.ok) toast({ title: 'Could not save', description: res.error, variant: 'destructive' });
    else toast({ title: 'Handle saved', description: `You'll post as @${handleInput.trim()}` });
  };

  const handlePickAvatar = async (id: string) => {
    setSavingAvatar(id);
    const res = await saveAvatar(id);
    setSavingAvatar(null);
    if (!res.ok) toast({ title: 'Could not save avatar', description: res.error, variant: 'destructive' });
  };

  return (
    <>
        {/* Community handle */}
        <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
          <div className="flex items-center gap-2 mb-2">
            <AtSign className="w-4 h-4 text-accent" />
            <h4 className="font-medium text-foreground">Community Handle</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            The pseudonym shown on your community posts. Real name and email stay private.
            3-30 characters, letters, numbers, underscore, dot.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Input
                value={handleInput}
                onChange={(e) => setHandleInput(e.target.value.replace(/[^A-Za-z0-9_.]/g, ''))}
                placeholder="your_handle"
                maxLength={30}
              />
              {checking ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2" />
              ) : available === true ? (
                <Check className="w-4 h-4 text-accent absolute right-3 top-1/2 -translate-y-1/2" />
              ) : null}
            </div>
            <Button
              onClick={handleSaveHandle}
              disabled={savingHandle || !handleInput.trim() || handleInput.trim() === handle || available === false}
            >
              {savingHandle && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              {handle ? 'Update' : 'Save'}
            </Button>
          </div>
          <div className="mt-5">
            <p className="text-sm text-muted-foreground mb-2">Avatar</p>
            <div className="flex flex-wrap gap-2">
              {AVATAR_PRESETS.map((a) => {
                const active = (avatarId ?? null) === a.id;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => handlePickAvatar(a.id)}
                    disabled={!handle || savingAvatar !== null}
                    aria-label={a.label}
                    title={a.label}
                    className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${a.className} ${active ? 'ring-2 ring-accent ring-offset-2 ring-offset-background scale-105' : 'opacity-70 hover:opacity-100'} ${!handle ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <a.Icon className="w-5 h-5" />
                  </button>
                );
              })}
            </div>
            {!handle && (
              <p className="text-xs text-muted-foreground mt-2">Save a handle first to choose an avatar.</p>
            )}
          </div>
        </Card>

        {/* Community location */}
        <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
          <h4 className="font-medium text-foreground mb-1">Community map location</h4>
          <p className="text-sm text-muted-foreground mb-4">
            We auto-detect your city from your IP. You can refine it or hide your pin entirely.
          </p>
          <LocationEditor />
        </Card>
    </>
  );
}
